import { api, CACHE_TTL } from 'services/rtk';
import {
  type ResponseOnlySuccess,
  type ResponseWithSuccess,
  type TypedBaseQueryError,
} from 'services/rtk/common';

import {
  type AddTaskCommentParams,
  type ChangeBoardTaskCommentParams,
  type CreateTaskParams,
  type FetchNewCommentsResponse,
  type FetchTaskCommentsResponse,
  type FetchTaskHistoryResponse,
  type FetchTasksFilters,
  type FetchTasksParams,
  type FetchTasksResponse,
  type FetchTasksSummaryResponse,
  type GetTasksCountersResponse,
  type ReadCommentsParams,
  type UpdateTaskParams,
} from './tasksBoardTypes';
import { type BoardTask } from '../../common/types/task';

export const tasksBoardApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'BoardTask',
      'BoardTasks',
      'BoardTasksSummary',
      'BoardTaskComments',
      'BoardTaskHistory',
      'BoardTasksCounters',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /* ========================
                  TASKS
       ========================== */
      fetchBoardTasks: build.query<FetchTasksResponse, FetchTasksParams>({
        query: (params) => ({
          url: '/board-tasks',
          params,
        }),
        providesTags: ['BoardTasks'],
      }),
      getTasksCounters: build.query<ResponseWithSuccess<GetTasksCountersResponse>, FetchTasksFilters>({
        query: (params) => ({
          url: '/board-tasks/counters',
          params,
        }),
        providesTags: ['BoardTasksCounters'],
      }),
      fetchBoardTasksSummary: build.query<FetchTasksSummaryResponse, void>({
        query: () => ({
          url: '/board-tasks/summary',
        }),
        providesTags: ['BoardTasksSummary'],
      }),
      fetchBoardTask: build.query<BoardTask, { id: number }>({
        query: ({ id }) => ({
          url: `/board-tasks/${id}`,
        }),
        transformResponse: (response: ResponseWithSuccess<BoardTask>) => {
          return response.data;
        },
        providesTags: (result, error, { id }) => [{ type: 'BoardTask', id: id }],
      }),
      createBoardTask: build.mutation<ResponseWithSuccess<BoardTask>, CreateTaskParams>({
        query: (params) => ({
          method: 'POST',
          url: '/board-tasks',
          body: params,
        }),
        invalidatesTags: ['BoardTasksSummary'],
      }),
      updateBoardTask: build.mutation<ResponseWithSuccess<BoardTask>, UpdateTaskParams>({
        query: ({ id, ...body }) => ({
          method: 'PATCH',
          url: `/board-tasks/${id}`,
          body: body,
        }),
        invalidatesTags: (result, error, { id }) => ['BoardTasksSummary', { type: 'BoardTask', id }],
        transformErrorResponse(response: TypedBaseQueryError) {
          if (response.data.error === 'NOT_ACTUAL_DATA') return 'Неактуальный статус задачи';
          return response;
        },
      }),
      readBoardTask: build.mutation<ResponseOnlySuccess, { id: number }>({
        query: ({ id }) => ({
          method: 'POST',
          url: `/board-tasks/${id}/read`,
        }),
        invalidatesTags: (result, error, { id }) => ['BoardTasksSummary', { type: 'BoardTask', id }],
      }),
      readAllBoardTasks: build.mutation<ResponseWithSuccess<{ ids: number[] }>, void>({
        query: () => ({
          method: 'POST',
          url: '/board-tasks/readAll',
        }),
        invalidatesTags: ['BoardTasksSummary'],
      }),
      fetchTaskHistory: build.query<FetchTaskHistoryResponse, { taskId: number }>({
        query: ({ taskId }) => ({
          url: `/board-tasks/${taskId}/history`,
        }),
        providesTags: (result, error, { taskId }) => [{ type: 'BoardTaskHistory', id: taskId }],
        keepUnusedDataFor: CACHE_TTL * 2,
      }),

      /* ========================
                COMMENTS
       ========================== */
      fetchBoardTaskComments: build.query<FetchTaskCommentsResponse, { taskId: number }>({
        query: ({ taskId }) => ({
          url: `/board-tasks/${taskId}/comments`,
        }),
        providesTags: (result, error, { taskId }) => [{ type: 'BoardTaskComments', id: taskId }],
        keepUnusedDataFor: CACHE_TTL * 2,
      }),
      addBoardTaskComment: build.mutation<ResponseWithSuccess<BoardTask>, AddTaskCommentParams>({
        query: ({ taskId, body, request }) => ({
          method: 'POST',
          url: `/board-tasks/${taskId}/comments`,
          body: {
            body,
            request,
          },
        }),
        invalidatesTags: (result, error, { taskId }) => [{ type: 'BoardTaskComments', id: taskId }],
      }),
      readBoardTaskComments: build.mutation<ResponseOnlySuccess, ReadCommentsParams>({
        query: ({ taskId, commentsIds }) => ({
          method: 'POST',
          url: `/board-tasks/${taskId}/comments/read`,
          body: {
            ids: commentsIds,
          },
        }),
        invalidatesTags: (result, error, { taskId }) => [{ type: 'BoardTaskComments', id: taskId }],
      }),
      deleteBoardTaskComment: build.mutation<ResponseOnlySuccess, { taskId: number; commentId: number }>({
        query: ({ taskId, commentId }) => ({
          method: 'DELETE',
          url: `/board-tasks/${taskId}/comments/${commentId}`,
        }),
        invalidatesTags: (result, error, { taskId }) => [{ type: 'BoardTaskComments', id: taskId }],
      }),
      changeBoardTaskComment: build.mutation<ResponseOnlySuccess, ChangeBoardTaskCommentParams>({
        query: ({ taskId, commentId, body }) => ({
          method: 'PATCH',
          url: `/board-tasks/${taskId}/comments/${commentId}`,
          body: { body },
        }),
        invalidatesTags: (result, error, { taskId }) => [{ type: 'BoardTaskComments', id: taskId }],
      }),
      fetchNewComments: build.query<FetchNewCommentsResponse, void>({
        query: () => ({
          url: '/board-tasks/newComments',
        }),
      }),

      /* ========================
                SCHEDULE
       ========================== */
      getWorktimeStatus: build.query<{ isWorkTime: boolean }, void>({
        query: () => ({
          url: '/work-schedule/state',
        }),
      }),
    }),
  });
